import React, { useEffect, useState } from 'react'
import { InputAdornment } from '@mui/material'
import ListingComponent from '../../Components/Molecule/ListingComponent'
import { MASTER_MANAGEMENT_CONSTANTS } from '../../Constants/Constant/Masters'
import {
  emptyFunction,
  combineErrorMessage,
  addOriginalUrlToChildren,
  getAllOriginalUrls,
  getSessionStorage,
  setSessionStorage,
  removeSessionStorage,
  toCamelCase,
} from '../../Helpers/Util'
import { useAppDispatch } from '../../Store/Hooks'
import { clearSystemConfigurationStore } from '../../Store/Slice/system-configuration.slice'
import styles from './MastersList.module.scss'
import { showAlert } from '../../Store/Slice/alert.slice'
import mainApiService from '../../Services'
import { MASTERS_MANAGEMENT } from '../../Constants/ApiDefinations'
import CONSTANTS, { PAGES_ROUTES } from '../../Constants'
import {
  setBlitzMasterConfiguration,
  setMaster,
  setSystemConfiguration,
} from '../../Store/Slice/masters.slice'
import updateTagMetaData from '../../Helpers/UpdateTagMetaData'
import useDebounce from '../../Hooks/useDebounce'
import { MUIFieldWithoutControl } from '../../Components/Shared'
import { Search } from '@mui/icons-material'

const SEARCH_STORAGE_KEY = 'masterListSearchText'
const LAST_ROUTE_KEY = 'masterListLastRoute'
const MASTERS_BASE_PATH = `/${PAGES_ROUTES.BoltMaster}/${PAGES_ROUTES.Masters}`

const getSafeSessionValue = (key: string): string => {
  try {
    return getSessionStorage(key) || ''
  } catch {
    sessionStorage.removeItem(key)
    return ''
  }
}

const MastersList: React.FC = () => {
  const dispatch = useAppDispatch()

  const [masterListData, setMasterListData] = useState<any[]>([])
  const [systemConfigListData, setSystemConfigListData] = useState<any[]>([])
  const [widgetLayoutConfigListData, setWidgetLayoutConfigListData] = useState<any[]>([])
  const [blitzMasterConfigListData, setBlitzMasterConfigListData] = useState<any[]>([])
  const [isLoader, setIsLoader] = useState<boolean>(true)
  const [searchText, setSearchText] = useState<string>(() => {
    const lastRoute = getSafeSessionValue(LAST_ROUTE_KEY)
    if (lastRoute?.startsWith(MASTERS_BASE_PATH)) {
      return getSafeSessionValue(SEARCH_STORAGE_KEY)
    }
    removeSessionStorage(SEARCH_STORAGE_KEY)
    return ''
  })

  const debouncedSearchText = useDebounce(searchText, 400)
  const hasNoResults =
    !isLoader &&
    masterListData.length === 0 &&
    systemConfigListData.length === 0 &&
    widgetLayoutConfigListData.length === 0 &&
    blitzMasterConfigListData.length === 0

  const getUserListFunction = async (search?: string) => {
    setIsLoader(true)
    const requestedData: any = MASTERS_MANAGEMENT.MASTER.LIST(search)
    const result: any = await mainApiService(requestedData)
    if (result?.responseCode === 200) {
      const masterData = addOriginalUrlToChildren(result?.data?.SegmentationMaster)
      const systemData = result?.data?.SystemConfiguration?.map((element: any) => ({
        ...element,
        originalUrl: element.url_key,
        url: `/${PAGES_ROUTES.BoltMaster}/${PAGES_ROUTES.Masters}/${element.url_key}`,
      }))
      const widgetLayoutConfiguration = result?.data?.LayoutConfiguration?.map((element: any) => ({
        ...element,
        originalUrl: element.url_key,
        url: `/${PAGES_ROUTES.BoltMaster}/${PAGES_ROUTES.Masters}/${element.url_key}`,
      }))
      const blitzMasterConfiguration = result?.data?.BlitzMasterConfiguration?.map(
        (element: any) => ({
          ...element,
          originalUrl: toCamelCase(element.url_key),
          url: `/${PAGES_ROUTES.BoltMaster}/${PAGES_ROUTES.Masters}/${PAGES_ROUTES.blitzMasterConfig}/${element.url_key}`,
        }),
      )

      dispatch(setMaster(getAllOriginalUrls(masterData)))
      setMasterListData(masterData)
      dispatch(setSystemConfiguration(systemData.map((ele: any) => ele.originalUrl)))
      dispatch(
        setBlitzMasterConfiguration(
          Object.fromEntries(
            blitzMasterConfiguration.map(
              ({ key, originalUrl }: { key: string; originalUrl: string }) => [
                originalUrl,
                key.split('/')[1],
              ],
            ),
          ),
        ),
      )
      setSystemConfigListData(systemData)
      setWidgetLayoutConfigListData(widgetLayoutConfiguration)
      setBlitzMasterConfigListData(blitzMasterConfiguration)
      setIsLoader(false)
    } else {
      dispatch(showAlert([true, combineErrorMessage(result), 'error']))
      setIsLoader(false)
    }
  }

  useEffect(() => {
    const clearOnPageRefresh = () => {
      removeSessionStorage(SEARCH_STORAGE_KEY)
      removeSessionStorage(LAST_ROUTE_KEY)
    }
    globalThis.addEventListener('beforeunload', clearOnPageRefresh)
    dispatch(clearSystemConfigurationStore({}) as any)

    return () => {
      globalThis.removeEventListener('beforeunload', clearOnPageRefresh)
      setSessionStorage(LAST_ROUTE_KEY, globalThis.location.pathname)
    }
  }, [])

  useEffect(() => {
    setSessionStorage(SEARCH_STORAGE_KEY, debouncedSearchText)
    getUserListFunction(debouncedSearchText)
  }, [debouncedSearchText])

  useEffect(() => {
    updateTagMetaData({ title: 'Master Lists' })
    return () => {
      updateTagMetaData({})
    }
  }, [])

  return (
    <div className={styles.mastersListWrapper}>
      <div className={styles.searchContainer}>
        <MUIFieldWithoutControl
          className='c-input listing-search'
          style={{ width: '300px' }}
          fullWidth
          name='master-listing-search'
          onChange={(e: any) => setSearchText(e)}
          placeholder='Search Master Lists'
          value={searchText}
          autoComplete='off'
          showClearIcon
          InputProps={{
            id: 'segmentedSearchId',
            startAdornment: (
              <InputAdornment position='start'>
                <Search style={{ color: '#B1B4B6' }} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {hasNoResults ? (
        <p className={styles.noResults}>No Master Lists found.</p>
      ) : (
        <>
          {masterListData.length > 0 && (
            <ListingComponent
              columns={MASTER_MANAGEMENT_CONSTANTS.MASTERS_COLUMNS}
              tableRows={masterListData}
              dataCount={0}
              isLoading={isLoader}
              headerBtnLabel={''}
              tableTitle={'Segment Master'}
              searchFunction={emptyFunction}
              showCreateButton={false}
              createButtonClick={emptyFunction}
              paginationOption={CONSTANTS.PAGINATION_OPTIONS}
              showSearchField={false}
              navigateToEdit={emptyFunction}
              isRowClickable={true}
              isCellClickable={false}
              isNestedRows={true}
            />
          )}

          {systemConfigListData.length > 0 && (
            <ListingComponent
              columns={MASTER_MANAGEMENT_CONSTANTS.MASTERS_COLUMNS}
              tableRows={systemConfigListData}
              dataCount={0}
              headerBtnLabel={''}
              isLoading={isLoader}
              tableTitle={'System Configuration'}
              searchFunction={emptyFunction}
              showCreateButton={false}
              createButtonClick={emptyFunction}
              paginationOption={CONSTANTS.PAGINATION_OPTIONS}
              showSearchField={false}
              navigateToEdit={emptyFunction}
              isRowClickable={true}
              isCellClickable={false}
            />
          )}

          {widgetLayoutConfigListData.length > 0 && (
            <ListingComponent
              columns={MASTER_MANAGEMENT_CONSTANTS.MASTERS_COLUMNS}
              tableRows={widgetLayoutConfigListData}
              dataCount={0}
              headerBtnLabel={''}
              isLoading={isLoader}
              tableTitle={'Layout Configuration'}
              searchFunction={emptyFunction}
              showCreateButton={false}
              createButtonClick={emptyFunction}
              paginationOption={CONSTANTS.PAGINATION_OPTIONS}
              showSearchField={false}
              navigateToEdit={emptyFunction}
              isRowClickable={true}
              isCellClickable={false}
            />
          )}

          {blitzMasterConfigListData.length > 0 && (
            <ListingComponent
              columns={MASTER_MANAGEMENT_CONSTANTS.MASTERS_COLUMNS}
              tableRows={blitzMasterConfigListData}
              dataCount={0}
              headerBtnLabel={''}
              isLoading={isLoader}
              tableTitle={'Blitz Master Configuration'}
              searchFunction={emptyFunction}
              showCreateButton={false}
              createButtonClick={emptyFunction}
              paginationOption={CONSTANTS.PAGINATION_OPTIONS}
              showSearchField={false}
              navigateToEdit={emptyFunction}
              isRowClickable={true}
              isCellClickable={false}
            />
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(MastersList)
